'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';

import { CardShared } from '@/app/components/card/shared';
import { PaginationDash } from '@/app/components/navigation/pagination';
import styles from '@/app/components/sections/special-offer/styles.module.css';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { type DataGAProps } from '@/app/utils/createDataGA';
import type { SpecialOffer } from '@/types/special-offer';

export interface OfferCarouselProps {
  data: SpecialOffer[];
  dataGA?: DataGAProps;
}

export const OfferCarousel = ({ data = [], dataGA }: OfferCarouselProps) => {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const { currentPage, onPageChange, scrollSnaps } =
    usePaginationSlider(emblaApi);

  if (data.length === 0) return null;

  return (
    <>
      <Sliders
        onEmblaInit={setEmblaApi}
        containerClassName="-ml-4 flex lg:-ml-6"
        loop={false}
        hidden={false}
        align="center"
        emblaOptions={{
          breakpoints: {
            '(min-width: 1024px)': {
              align: 'start',
            },
          },
        }}
      >
        {data.map((item, index) => {
          const currentCategory = item.category?.[0];
          const basePath = `/specialoffer/${currentCategory?.url || ''}`;
          return (
            <div
              key={index}
              className={cn(
                'pl-4 lg:pl-6',
                'max-w-98 lg:max-w-125',
                styles.slideOffer
              )}
              {...createDataAttributes({
                test: `item-${index}`,
              })}
            >
              <CardShared
                key={index}
                title={item.name || ''}
                href={`${basePath}/${item.url}`}
                image={{
                  src: item.img?.url || '',
                  alt: item?.name || '',
                  width: 600,
                  height: 338,
                  aspectRatio: 'aspect-16-9',
                  sizes:
                    '(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 90vw',
                }}
                tag={{
                  href: basePath,
                  label: currentCategory?.name || '',
                }}
                date={{
                  startDate: item.begin_date || '',
                  endDate: item.end_date || '',
                }}
                untilText
                dataGA={{ text: item.name, click: true, ...dataGA }}
              />
            </div>
          );
        })}
      </Sliders>
      {scrollSnaps.length > 1 && (
        <PaginationDash
          wrapperClassName="mt-8 mb-8 flex justify-center md:mt-6 md:mb-0 xl:mt-10"
          theme="dark"
          totalPages={scrollSnaps.length}
          currentPage={currentPage}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};
